import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import dayjs, { type Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import React, { useEffect, useMemo, useRef } from 'react'
import { DayPicker, type DayButtonProps, type Matcher, type MonthCaptionProps, type NavProps } from 'react-day-picker'

import Icon from '@/legacy-src/components/icons/Icon'
import { Button } from '@/legacy-src/components/ui/button'
import useEvents from '@/legacy-src/hooks/useEvents'
import { cn } from '@/legacy-src/lib/utils'
import { capitalize } from '@/legacy-src/utilities/textUtilities'

import { useCart } from '@/src/domains/cart/hooks/useCart'
import { useClientBookingFreeSlots } from '@/src/domains/client-booking/hooks/useClientBookingFreeSlots'
import type { FreeSlotTypeFilterWithAvailability } from '@/src/domains/client-booking/types'

dayjs.extend(isBetween)

function DayButton({ day, modifiers, className, ...props }: DayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      {...props}
      ref={ref}
      className={cn(
        'relative size-9 border-0 bg-blue-500 p-0 text-petrol-500 hover:bg-petrol-300 hover:text-white',
        modifiers.disabled && 'bg-transparent text-sand-700 !opacity-100',
        modifiers.selected && 'bg-petrol-500 text-white !opacity-100 hover:bg-petrol-500',
        modifiers.today &&
          modifiers.disabled &&
          'border-2 border-solid border-sand-700 bg-transparent text-sand-700 !opacity-100',
        className,
      )}
      size="small"
      disabled={modifiers.selected || modifiers.disabled}
    >
      {day.date.getDate()}
      {(modifiers.hasAppointment || modifiers.hasSelectedSlot) && (
        <div className="absolute inset-x-0 bottom-1 left-0 flex h-1.5 justify-center gap-0.5">
          {modifiers.hasAppointment && (
            <div className={cn('size-1.5 rounded-full bg-primary', modifiers.disabled && 'bg-sand-700')} />
          )}
          {modifiers.hasSelectedSlot && (
            <div className={cn('size-1.5 rounded-full bg-petrol-500', modifiers.selected && 'bg-white')} />
          )}
        </div>
      )}
    </Button>
  )
}

function MonthCaption({ calendarMonth, displayIndex, className, children, ...props }: MonthCaptionProps) {
  return (
    <div {...props} className={cn('mb-6 flex h-8 items-center justify-center gap-2', className)}>
      <Icon icon="calendar" size="md" color="primary" className="mr-2" />
      <div className="font-bold">{children}</div>
    </div>
  )
}

function Nav({ nextMonth, onNextClick, previousMonth, onPreviousClick, className, ...props }: NavProps) {
  const isPreviousDisabled = !previousMonth

  return (
    <div {...props} className={cn('-mb-8 grid grid-cols-7', className)}>
      <div className="flex items-center justify-center">
        <Button
          size="small"
          className="size-8 p-0 text-petrol-500 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-35"
          variant="ghost"
          onClick={onPreviousClick}
          disabled={isPreviousDisabled}
        >
          <Icon icon="before" size="xxs" />
        </Button>
      </div>
      {/* This empty grid cell is intentionally used for spacing between the navigation buttons */}
      <div className="col-span-5"></div>
      <div className="flex items-center justify-center">
        <Button size="small" className="size-8 p-0 text-petrol-500" variant="ghost" onClick={onNextClick}>
          <Icon icon="next" size="xxs" />
        </Button>
      </div>
    </div>
  )
}

type Props = {
  therapistId: number
  serviceId: number
  type: FreeSlotTypeFilterWithAvailability
  day: Dayjs | null
  setDay: (day: Dayjs) => void
  month: Dayjs
  setMonth: (month: Dayjs) => void
}

const CalendarMonthView: React.FC<Props> = ({ day, setDay, month, setMonth, therapistId, serviceId, type }) => {
  const { selectedSlots } = useCart()

  const currentMonth = dayjs().startOf('month')
  const endOfMonth = month.endOf('month').endOf('day')

  const { slots, isLoading: isLoadingSlots } = useClientBookingFreeSlots({
    therapistId,
    serviceId,
    type,
    dateTo: endOfMonth.unix(),
  })

  const { events, isLoading: isLoadingEvents } = useEvents()

  const disabledMatcher: Matcher = useMemo(() => {
    if (isLoadingSlots || isLoadingEvents) return true

    const daysInMonth = month.daysInMonth()
    const enabledDates = {}

    // Enable days with available slots
    if (slots) {
      slots.forEach((slot) => {
        const dayjsStartDay = dayjs(slot.startDate)

        if (dayjsStartDay.isBetween(month, endOfMonth)) {
          enabledDates[dayjsStartDay.date()] = true
        }
      })
    }

    if (events) {
      events
        .filter(
          (event) =>
            event.status === EVENT_STATUS.RESERVED &&
            event.serviceId === serviceId &&
            event.therapistId === therapistId,
        )
        .forEach((event) => {
          const startDate = dayjs(event.startDate)

          if (startDate.isBetween(month, endOfMonth)) {
            enabledDates[startDate.date()] = true
          }
        })
    }

    const matcher: Matcher = []

    for (let day = 1; day <= daysInMonth; day++) {
      if (!enabledDates[day]) {
        matcher.push(month.date(day).toDate())
      }
    }

    return matcher
  }, [isLoadingSlots, slots, isLoadingEvents, events, month, serviceId, therapistId, endOfMonth])

  const hasAppointmentMatcher: Matcher = useMemo(() => {
    if (isLoadingEvents || !events) return false

    return events
      .filter(
        (event) =>
          (event.status === EVENT_STATUS.COMPLETED || event.status === EVENT_STATUS.RESERVED) &&
          event.serviceId === serviceId &&
          event.therapistId === therapistId,
      )
      .map((event) => dayjs(event.startDate).toDate())
  }, [isLoadingEvents, events, serviceId, therapistId])

  const hasSelectedSlotMatcher: Matcher = useMemo(() => {
    if (isLoadingSlots || !slots) return false

    return selectedSlots
      .map(({ startDate }) => slots.find((slot) => slot.startDate === startDate))
      .filter((slot) => !!slot)
      .map((slot) => dayjs(slot.startDate).toDate())
  }, [isLoadingSlots, slots, selectedSlots])

  return (
    <DayPicker
      mode="single"
      selected={day ? day.toDate() : new Date()}
      onSelect={(day) => setDay(dayjs(day))}
      month={day ? month.toDate() : new Date()}
      onMonthChange={(month) => {
        const selectedMonth = dayjs(month).startOf('month')

        setMonth(selectedMonth.isBefore(currentMonth, 'month') ? currentMonth : selectedMonth)
      }}
      startMonth={currentMonth.toDate()}
      weekStartsOn={1}
      modifiers={{
        disabled: disabledMatcher,
        hasAppointment: hasAppointmentMatcher,
        hasSelectedSlot: hasSelectedSlotMatcher,
      }}
      components={{
        DayButton,
        MonthCaption,
        Nav,
      }}
      classNames={{
        root: 'mb-6',
        day: 'laptop:py-2 tablet:py-2 py-0.5 text-center',
        month_grid: 'w-full',
        weekday: 'text-center font-normal text-sm pb-4 uppercase',
        month_caption: 'font-normal text-normal font-serif',
      }}
      formatters={{
        formatWeekdayName: (date) => dayjs(date).format('dd'),
        formatCaption: (date) => capitalize(dayjs(date).format('MMMM YYYY')),
      }}
      labels={{
        labelWeekday: (date) => capitalize(dayjs(date).format('dddd')),
        labelGrid: (date) => capitalize(dayjs(date).format('MMMM YYYY')),
        labelDayButton: (date) => capitalize(dayjs(date).format('dddd, DD. MM. YYYY')),
      }}
    />
  )
}

export default CalendarMonthView
